// material-ui
import {
    Box,
    Grid,
    Button,
    Typography,
    TextField,
    Modal,
    CircularProgress,
    Autocomplete,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Divider,
    IconButton
} from '@mui/material';
import { deepPurple, blue } from '@mui/material/colors';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import EditIcon from '@mui/icons-material/Edit';

import { useState, useEffect } from 'react';
import { axiosInstance } from 'utils/axios';
import { useSelector } from 'react-redux';
import { selectCurrentToken } from 'features/auth/authSlice';
import { successAlert, errorAlert } from 'ui-component/Toasts';
import { useTranslation } from "react-i18next";

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: 900,
    width: '90%',
    maxHeight: '90vh',
    overflowY: 'auto',
    bgcolor: 'background.paper',
    boxShadow: 24,
    padding: '20px',
    borderRadius: '10px'
};

const CustomIPODetailsModal = ({
    customerIPOModalOpen,
    setCustomerIPOModalOpen,
    customerRow,
    handleRefresh
}) => {
    const { t } = useTranslation();
    const token = useSelector(selectCurrentToken);

    const [loading, setLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Data States
    const [customIPOs, setCustomIPOs] = useState([]);
    const [cryptoSymbols, setCryptoSymbols] = useState([]);

    // Form States
    const [editingId, setEditingId] = useState(0); // 0 = POST, > 0 = PUT
    const [selectedSymbol, setSelectedSymbol] = useState(null);
    const [launchDate, setLaunchDate] = useState('');
    const [minAmount, setMinAmount] = useState('');
    const [tradeStartDate, setTradeStartDate] = useState('');

    useEffect(() => {
        if (customerIPOModalOpen && customerRow?.id) {
            fetchCustomIPOs();
            fetchCryptoSymbols();
            clearForm();
        }
    }, [customerIPOModalOpen, customerRow]);

    const fetchCustomIPOs = async () => {
        try {
            setLoading(true);
            const res = await axiosInstance.get(`/CustomerCustomIPOLaunchDetails?customerID=${customerRow.id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.status === 200) {
                setCustomIPOs(res.data);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const fetchCryptoSymbols = async () => {
        try {
            const res = await axiosInstance.get(`/CryptoSymbols/All`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.status === 200) {
                setCryptoSymbols(res.data);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const clearForm = () => {
        setEditingId(0);
        setSelectedSymbol(null);
        setLaunchDate('');
        setMinAmount('');
        setTradeStartDate('');
    };

    const handleEditClick = (ipo) => {
        setEditingId(ipo.id);
        // Find the full symbol object from the list to populate the Autocomplete
        const symbolObj = cryptoSymbols.find(s => s.id === ipo.cryptoSymbolID) || { id: ipo.cryptoSymbolID, code: ipo.cryptoSymbolCode };
        setSelectedSymbol(symbolObj);
        
        // Format ISO string to local datetime-local format (YYYY-MM-DDTHH:mm)
        setLaunchDate(ipo.launchDate ? ipo.launchDate.slice(0, 16) : '');
        setMinAmount(ipo.minAmountBeforeLaunchDate);
        setTradeStartDate(ipo.tradeStartDate ? ipo.tradeStartDate.slice(0, 16) : '');
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);

        // Helper to keep exact local time but format it with 'Z' for the backend
        const toLocalISOString = (dateString) => {
            if (!dateString) return null;
            const date = new Date(dateString);
            return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString();
        };

        try {
            let res;
            if (editingId > 0) {
                // PUT - Update Existing
                res = await axiosInstance.put('/CustomerCustomIPOLaunchDetails', {
                    id: editingId,
                    // launchDate: toLocalISOString(launchDate),
                    // minAmountBeforeLaunchDate: parseFloat(minAmount),
                    tradeStartDate: toLocalISOString(tradeStartDate)
                }, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            } else {
                // POST - Insert New
                if (!selectedSymbol) {
                    errorAlert('Please select a Crypto Symbol');
                    setIsSubmitting(false);
                    return;
                }
                res = await axiosInstance.post('/CustomerCustomIPOLaunchDetails', {
                    customerID: customerRow.id,
                    cryptoSymbolID: selectedSymbol.id,
                    // launchDate: toLocalISOString(launchDate),
                    // minAmountBeforeLaunchDate: parseFloat(minAmount),
                    tradeStartDate: toLocalISOString(tradeStartDate)
                }, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            }

            if (res.status === 200 || res.status === 201) {
                successAlert(editingId > 0 ? 'Successfully Updated' : 'Successfully Added');
                clearForm();
                fetchCustomIPOs(); // Refresh the list in the modal
                if (handleRefresh) handleRefresh(); // Refresh parent table if needed
            }
        } catch (error) {
            errorAlert('Something went wrong.');
            console.error(error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div>
            {/* Open Modal Button */}
            <Button
                onClick={() => setCustomerIPOModalOpen(true)}
                sx={{
                    border: 'none',
                    backgroundColor: 'transparent',
                    width: 'fit-content',
                    height: 'fit-content',
                    color: deepPurple[500]
                }}
                title="Custom IPO Details"
            >
                <AttachMoneyIcon />
            </Button>

            {/* Modal */}
            <Modal 
                open={customerIPOModalOpen} 
                onClose={() => setCustomerIPOModalOpen(false)}
            >
                <Box sx={style}>
                    <Typography variant="h4" sx={{ mb: 3 }}>
                        Custom IPO Details - {customerRow?.username || 'Customer'}
                    </Typography>

                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            {/* --- TOP: Existing Custom IPOs Table --- */}
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                Existing Custom IPOs
                            </Typography>
                            <TableContainer component={Paper} sx={{ mb: 3, maxHeight: 250 }}>
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Symbol</TableCell>
                                            {/* <TableCell>Launch Date</TableCell> */}
                                            {/* <TableCell>Min Amount</TableCell> */}
                                            <TableCell>Trade Start Date</TableCell>
                                            <TableCell align="center">Action</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {customIPOs.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={5} align="center">No custom IPOs found.</TableCell>
                                            </TableRow>
                                        ) : (
                                            customIPOs.map((row) => (
                                                <TableRow key={row.id}>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>{row.cryptoSymbolCode}</TableCell>
                                                    {/* <TableCell>{row.launchDate ? row.launchDate.replace('T', ' ').substring(0, 16) : '-'}</TableCell> */}
                                                    {/* <TableCell>${row.minAmountBeforeLaunchDate}</TableCell> */}
                                                    <TableCell>{row.tradeStartDate ? row.tradeStartDate.replace('T', ' ').substring(0, 16) : '-'}</TableCell>
                                                    <TableCell align="center">
                                                        <IconButton color="primary" onClick={() => handleEditClick(row)}>
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <Divider sx={{ mb: 3 }} />

                            {/* --- BOTTOM: Form to Add / Edit --- */}
                            <Typography variant="h5" sx={{ mb: 2, color: editingId > 0 ? blue[700] : 'text.primary' }}>
                                {editingId > 0 ? "Edit Custom IPO" : "Add New Custom IPO"}
                            </Typography>

                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <Autocomplete
                                        options={cryptoSymbols}
                                        getOptionLabel={(option) => option.code || ''}
                                        value={selectedSymbol}
                                        onChange={(event, newValue) => setSelectedSymbol(newValue)}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        disabled={editingId > 0} // Cannot change symbol when updating
                                        renderInput={(params) => (
                                            <TextField 
                                                {...params} 
                                                label="Crypto Symbol" 
                                                required 
                                            />
                                        )}
                                    />
                                </Grid>
                                {/* <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label="Min Amount Before Launch"
                                        required
                                        value={minAmount}
                                        onChange={(e) => setMinAmount(e.target.value)}
                                    />
                                </Grid> */}
                                {/* <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        type="datetime-local"
                                        label="Launch Date"
                                        required
                                        InputLabelProps={{ shrink: true }}
                                        value={launchDate}
                                        onChange={(e) => setLaunchDate(e.target.value)}
                                    />
                                </Grid> */}
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        type="datetime-local"
                                        label="Trade Start Date"
                                        required
                                        InputLabelProps={{ shrink: true }}
                                        value={tradeStartDate}
                                        onChange={(e) => setTradeStartDate(e.target.value)}
                                    />
                                </Grid>
                            </Grid>

                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 4 }}>
                                {editingId > 0 && (
                                    <Button variant="outlined" onClick={clearForm} disabled={isSubmitting}>
                                        Cancel Edit
                                    </Button>
                                )}
                                <Button
                                    variant="contained"
                                    onClick={handleSubmit}
                                    // disabled={isSubmitting || !selectedSymbol || !launchDate || !tradeStartDate || minAmount === ''}
                                    disabled={isSubmitting || !selectedSymbol || !tradeStartDate}
                                    sx={{ width: '150px' }}
                                >
                                    {isSubmitting ? <CircularProgress size={24} color="inherit" /> : (editingId > 0 ? "Update" : "Submit")}
                                </Button>
                            </Box>
                        </>
                    )}
                </Box>
            </Modal>
        </div>
    );
};

export default CustomIPODetailsModal;